import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, appId } from '../../../firebase-config';
import { getActiveCompanyId } from '../../../core/company/companyConfig';
import { resolveContactsCollectionPath } from '../../../core/company/companyPaths';
import { Icon, ARGENTINA_PROVINCES, geocodeAddress } from './LogisticsUtils';

// =========================
// Helpers
// =========================
const DEFAULT_COMPANY_ID = 'default';
const COMMON_TYPES = ['cliente', 'proveedor', 'transporte'];
const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '';
const PROVIDER_TYPE_HINTS = ['proveedor', 'corralon', 'corralon', 'distribuidor', 'mayorista', 'fabricante', 'deposito'];
const CLIENT_TYPE_HINTS = ['cliente', 'destino', 'obra', 'sucursal', 'punto-de-entrega'];
const TRANSPORT_TYPE_HINTS = ['transporte', 'fletero', 'flete', 'correo', 'logistica', 'mensajeria'];

const normalizeType = (type) => {
  const normalized = (type || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || 'cliente';
};

const resolveContactTypeFamily = (type = '') => {
  const normalized = normalizeType(type);
  if (COMMON_TYPES.includes(normalized)) return normalized;
  if (PROVIDER_TYPE_HINTS.some((hint) => normalized.includes(hint))) return 'proveedor';
  if (TRANSPORT_TYPE_HINTS.some((hint) => normalized.includes(hint))) return 'transporte';
  if (CLIENT_TYPE_HINTS.some((hint) => normalized.includes(hint))) return 'cliente';
  return normalized;
};

const normalizeString = (value) => (value || '').toString().trim();
const formatTypeLabel = (type = '') =>
  normalizeString(type)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const pluralizeTypeLabel = (type = '') => {
  const label = formatTypeLabel(type);
  if (!label) return 'Registros';
  return /s$/i.test(label) ? label : `${label}s`;
};

const buildContactKey = (contact) => {
  const name = normalizeString(contact?.name).toLowerCase();
  const type = normalizeType(contact?.type);
  return `${name}::${type}`;
};

const resolveCompanyContactsPathSafe = (companyId) => {
  try {
    return resolveContactsCollectionPath(companyId);
  } catch (errorByString) {
    try {
      return resolveContactsCollectionPath({ appId, companyId });
    } catch (errorByObject) {
      console.warn(
        'No se pudo resolver path de contactos por empresa. Se usa fallback legacy.',
        { errorByString, errorByObject, companyId }
      );
      return `artifacts/${appId}/public/data/contacts`;
    }
  }
};

const normalizeAddresses = (addresses = []) => {
  if (!Array.isArray(addresses) || addresses.length === 0) {
    return [
      {
        address: '',
        apartment: '',
        locality: '',
        province: 'CABA',
        partido: '',
        postalCode: '',
        latlng: null
      }
    ];
  }

  return addresses.map((addr) => ({
    address: addr?.address || '',
    apartment: addr?.apartment || '',
    locality: addr?.locality || '',
    province: addr?.province || 'CABA',
    partido: addr?.partido || '',
    postalCode: addr?.postalCode || '',
    latlng: addr?.latlng || null
  }));
};

const normalizeContactPeople = (contacts = []) => {
  if (!Array.isArray(contacts) || contacts.length === 0) {
    return [{ name: '', phone: '' }];
  }

  return contacts.map((c) => ({
    name: c?.name || '',
    phone: c?.phone || ''
  }));
};

const sanitizeAddressesForSave = (addresses = []) => {
  const cleaned = (addresses || [])
    .map((addr) => ({
      address: normalizeString(addr?.address),
      apartment: normalizeString(addr?.apartment),
      locality: normalizeString(addr?.locality),
      province: normalizeString(addr?.province) || 'CABA',
      partido: normalizeString(addr?.partido),
      postalCode: normalizeString(addr?.postalCode),
      latlng: addr?.latlng || null
    }))
    .filter(
      (addr) =>
        addr.address ||
        addr.apartment ||
        addr.locality ||
        addr.partido ||
        addr.postalCode
    );

  return cleaned.length > 0
    ? cleaned
    : [
        {
          address: '',
          apartment: '',
          locality: '',
          province: 'CABA',
          partido: '',
          postalCode: '',
          latlng: null
        }
      ];
};

const sanitizeContactsForSave = (contacts = []) => {
  const cleaned = (contacts || [])
    .map((c) => ({
      name: normalizeString(c?.name),
      phone: normalizeString(c?.phone)
    }))
    .filter((c) => c.name || c.phone);

  return cleaned.length > 0 ? cleaned : [{ name: '', phone: '' }];
};

const mergeContacts = (companyContacts, legacyContacts) => {
  const mergedMap = new Map();

  legacyContacts.forEach((contact) => {
    mergedMap.set(buildContactKey(contact), contact);
  });

  companyContacts.forEach((contact) => {
    mergedMap.set(buildContactKey(contact), contact);
  });

  return Array.from(mergedMap.values()).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
  );
};

const TYPE_TONES = {
  cliente: 'bg-blue-100 text-blue-700 border-blue-200',
  proveedor: 'bg-violet-100 text-violet-700 border-violet-200',
  transporte: 'bg-emerald-100 text-emerald-700 border-emerald-200'
};
const resolveTypeTone = (type = '') =>
  TYPE_TONES[resolveContactTypeFamily(type)] || 'bg-slate-100 text-slate-700 border-slate-200';

const buildAddressLabel = (addr = {}) =>
  [
    normalizeString(addr?.address),
    normalizeString(addr?.apartment),
    normalizeString(addr?.locality),
    normalizeString(addr?.partido),
    normalizeString(addr?.province) || 'CABA',
    normalizeString(addr?.postalCode),
    'Argentina'
  ]
    .filter(Boolean)
    .join(', ');

const createAddressValidationState = ({
  status = GEOAPIFY_API_KEY ? 'idle' : 'missing-key',
  text,
  validatedAddress = ''
} = {}) => ({
  status,
  text:
    text ||
    (GEOAPIFY_API_KEY
      ? 'Completá la dirección y validala para dejar este registro listo para rutas, salidas y compras.'
      : 'Completá la dirección y validala manualmente para guardar una ubicación operativa confiable.'),
  validatedAddress
});

const Badge = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-slate-100 text-slate-700',
    blue: 'border-blue-200 bg-blue-100 text-blue-700',
    violet: 'border-violet-200 bg-violet-100 text-violet-700',
    emerald: 'border-emerald-200 bg-emerald-100 text-emerald-700'
  };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
};

// =========================
// Modal de contacto
// =========================
const ContactModal = ({
  contact,
  onSave,
  onClose,
  onDelete,
  showMessage,
  availableTypes = []
}) => {
  const normalizedContact = contact
    ? {
        ...contact,
        type: normalizeType(contact?.type),
        addresses: normalizeAddresses(contact?.addresses),
        contacts: normalizeContactPeople(contact?.contacts)
      }
    : {};

  const [formData, setFormData] = useState({
    name: '',
    type: 'cliente',
    addresses: [
      {
        address: '',
        apartment: '',
        locality: '',
        province: 'CABA',
        partido: '',
        postalCode: '',
        latlng: null
      }
    ],
    contacts: [{ name: '', phone: '' }],
    ...normalizedContact
  });
  const normalizedAvailableTypes = useMemo(
    () =>
      Array.from(
        new Set(
          [...COMMON_TYPES, ...availableTypes, normalizedContact?.type]
            .map((type) => normalizeType(type))
            .filter(Boolean)
        )
      ),
    [availableTypes, normalizedContact?.type]
  );
  const [isCustomTypeMode, setIsCustomTypeMode] = useState(() => {
    const currentType = normalizeType(normalizedContact?.type);
    return Boolean(normalizedContact?.type) && !normalizedAvailableTypes.includes(currentType);
  });
  const [addressValidationByIndex, setAddressValidationByIndex] = useState(() =>
    normalizeAddresses(normalizedContact.addresses).reduce((acc, addr, index) => {
      acc[index] = createAddressValidationState(
        addr?.latlng
          ? {
              status: 'success',
              text: 'Ubicación validada para este registro.',
              validatedAddress: buildAddressLabel(addr)
            }
          : {}
      );
      return acc;
    }, {})
  );

  const isNew = !contact;

  useEffect(() => {
    setAddressValidationByIndex((prev) =>
      formData.addresses.reduce((acc, addr, index) => {
        const previous = prev[index];
        if (addr?.latlng) {
          acc[index] = createAddressValidationState({
            status: 'success',
            text: 'Ubicación validada para este registro.',
            validatedAddress: buildAddressLabel(addr)
          });
          return acc;
        }
        acc[index] =
          previous && previous.status !== 'success'
            ? previous
            : createAddressValidationState();
        return acc;
      }, {})
    );
  }, [formData.addresses]);

  const handleFieldChange = (e, index, field, section) => {
    const value = e.target.value;

    setFormData((prev) => {
      const newData = { ...prev };
      newData[section] = [...newData[section]];
      newData[section][index] = { ...newData[section][index], [field]: value };

      if (
        section === 'addresses' &&
        ['address', 'apartment', 'locality', 'partido', 'province', 'postalCode'].includes(field)
      ) {
        newData[section][index].latlng = null;
        setAddressValidationByIndex((prevValidation) => ({
          ...prevValidation,
          [index]: createAddressValidationState({
            text: 'La dirección cambió. Validala de nuevo para guardar coordenadas reales.',
            validatedAddress: ''
          })
        }));
      }

      return newData;
    });
  };

  const addField = (section) => {
    setFormData((prev) => {
      const newData = { ...prev };

      if (section === 'addresses') {
        newData.addresses = [
          ...newData.addresses,
          {
            address: '',
            apartment: '',
            locality: '',
            province: 'CABA',
            partido: '',
            postalCode: '',
            latlng: null
          }
        ];
        setAddressValidationByIndex((prevValidation) => ({
          ...prevValidation,
          [newData.addresses.length - 1]: createAddressValidationState()
        }));
      } else {
        newData.contacts = [...newData.contacts, { name: '', phone: '' }];
      }

      return newData;
    });
  };

  const removeField = (index, section) => {
    setFormData((prev) => {
      const newData = { ...prev };

      if (newData[section].length > 1) {
        newData[section] = newData[section].filter((_, i) => i !== index);
        if (section === 'addresses') {
          setAddressValidationByIndex((prevValidation) =>
            Object.fromEntries(
              Object.entries(prevValidation)
                .filter(([key]) => Number(key) !== index)
                .map(([key, value]) => [Number(key) > index ? Number(key) - 1 : Number(key), value])
            )
          );
        }
      }

      return newData;
    });
  };

  const handleGeocodeAddress = async (index) => {
    const addr = formData.addresses[index];

    if (!normalizeString(addr?.address)) {
      setAddressValidationByIndex((prevValidation) => ({
        ...prevValidation,
        [index]: createAddressValidationState({
          status: 'error',
          text: 'Completá la dirección antes de validarla.',
          validatedAddress: ''
        })
      }));
      return null;
    }

    if (addr.latlng) {
      setAddressValidationByIndex((prevValidation) => ({
        ...prevValidation,
        [index]: createAddressValidationState({
          status: 'success',
          text: 'Ubicación validada para este registro.',
          validatedAddress: buildAddressLabel(addr)
        })
      }));
      return addr.latlng;
    }

    setAddressValidationByIndex((prevValidation) => ({
      ...prevValidation,
      [index]: createAddressValidationState({
        status: 'loading',
        text: 'Validando ubicación en mapa...',
        validatedAddress: ''
      })
    }));

    const coords = await geocodeAddress(buildAddressLabel(addr), showMessage);

    if (coords) {
      setFormData((prev) => {
        const newData = { ...prev };
        newData.addresses = [...newData.addresses];
        newData.addresses[index] = { ...newData.addresses[index], latlng: coords };
        return newData;
      });
      setAddressValidationByIndex((prevValidation) => ({
        ...prevValidation,
        [index]: createAddressValidationState({
          status: 'success',
          text: 'Ubicación validada y lista para reutilizar.',
          validatedAddress: buildAddressLabel(addr)
        })
      }));
      return coords;
    }

    setAddressValidationByIndex((prevValidation) => ({
      ...prevValidation,
      [index]: createAddressValidationState({
        status: 'error',
        text: 'No pude validar esta dirección. Revisala antes de guardar.',
        validatedAddress: ''
      })
    }));
    return null;
  };

  const handleSave = async () => {
    if (!normalizeString(formData.name)) {
      showMessage?.('El nombre del contacto es obligatorio.', 'warning');
      return;
    }

    if (isCustomTypeMode && !normalizeString(formData.type)) {
      showMessage?.('Escribí el nombre de la nueva categoría antes de guardar.', 'warning');
      return;
    }

    const updatedAddresses = await Promise.all(
      formData.addresses.map(async (addr, index) => {
        if (addr.address && !addr.latlng) {
          const coords = await handleGeocodeAddress(index);
          return { ...addr, latlng: coords || null };
        }

        return addr;
      })
    );

    const invalidAddress = updatedAddresses.find(
      (addr) => normalizeString(addr?.address) && !addr?.latlng
    );
    if (invalidAddress) {
      showMessage?.(
        'Antes de guardar este cliente, proveedor o transporte, validá cada dirección para dejar coordenadas reales.',
        'warning'
      );
      return;
    }

    onSave(
      {
        ...formData,
        name: normalizeString(formData.name),
        type: normalizeType(formData.type),
        addresses: sanitizeAddressesForSave(updatedAddresses),
        contacts: sanitizeContactsForSave(formData.contacts)
      },
      isNew
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-[2px]">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500">
              Base operativa
            </p>
            <h3 className="mt-2 text-2xl font-bold tracking-[-0.03em] text-slate-900">
              {isNew ? 'Nuevo registro' : `Editar ${formData.name}`}
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Guardá contactos, sucursales y datos operativos reutilizables para futuras rutas.
            </p>
          </div>

          <button
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
          >
            <Icon path="M6 18L18 6M6 6l12 12" className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-1 gap-6">
            <section className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                Identidad del registro
              </p>

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Nombre
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nombre de empresa o persona"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Tipo
                  </label>
                  <select
                    value={isCustomTypeMode ? '__new__' : normalizeType(formData.type)}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      if (nextValue === '__new__') {
                        setIsCustomTypeMode(true);
                        setFormData((prev) => ({ ...prev, type: '' }));
                        return;
                      }
                      setIsCustomTypeMode(false);
                      setFormData((prev) => ({ ...prev, type: nextValue }));
                    }}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                  >
                    {normalizedAvailableTypes.map((type) => (
                      <option key={type} value={type}>
                        {formatTypeLabel(type)}
                      </option>
                    ))}
                    <option value="__new__">Nueva categoría</option>
                  </select>
                  {isCustomTypeMode && (
                    <input
                      type="text"
                      value={formData.type}
                      onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}
                      placeholder="Ej: depósito, taller, operador"
                      className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                    />
                  )}
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">
                    Elegí una categoría existente o creá una nueva para ordenar mejor tu base de datos.
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                    Direcciones y sucursales
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    Registrá una o más ubicaciones para reutilizarlas en el armado de rutas.
                  </p>
                </div>

                <button
                  onClick={() => addField('addresses')}
                  className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
                >
                  <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                  Añadir sucursal
                </button>
              </div>

              <div className="mt-4 space-y-4">
                {formData.addresses.map((addr, index) => (
                  <div
                    key={index}
                    className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <Badge tone="slate">Sucursal {index + 1}</Badge>

                      {formData.addresses.length > 1 && (
                        <button
                          onClick={() => removeField(index, 'addresses')}
                          className="text-sm font-semibold text-rose-600 transition hover:text-rose-700"
                        >
                          Eliminar sucursal
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Dirección
                        </label>
                        <input
                          type="text"
                          value={addr.address}
                          onChange={(e) => handleFieldChange(e, index, 'address', 'addresses')}
                          placeholder="Calle y altura"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                          onBlur={() => handleGeocodeAddress(index)}
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Piso / Depto / Casa
                        </label>
                        <input
                          type="text"
                          value={addr.apartment}
                          onChange={(e) => handleFieldChange(e, index, 'apartment', 'addresses')}
                          placeholder="Casa, depto o piso"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Provincia
                        </label>
                        <select
                          value={addr.province}
                          onChange={(e) => handleFieldChange(e, index, 'province', 'addresses')}
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                        >
                          {ARGENTINA_PROVINCES.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      </div>

                      {addr.province === 'Buenos Aires' && (
                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">
                            Partido
                          </label>
                          <input
                            type="text"
                            value={addr.partido}
                            onChange={(e) => handleFieldChange(e, index, 'partido', 'addresses')}
                            placeholder="Partido"
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                          />
                        </div>
                      )}

                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Localidad
                        </label>
                        <input
                          type="text"
                          value={addr.locality}
                          onChange={(e) => handleFieldChange(e, index, 'locality', 'addresses')}
                          placeholder="Localidad"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Código postal
                        </label>
                        <input
                          type="text"
                          value={addr.postalCode}
                          onChange={(e) => handleFieldChange(e, index, 'postalCode', 'addresses')}
                          placeholder="Código postal"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                        />
                      </div>
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Validación de ubicación
                          </p>
                          <p className="mt-1 text-sm text-slate-600">
                            {addressValidationByIndex[index]?.text || createAddressValidationState().text}
                          </p>
                          {addressValidationByIndex[index]?.validatedAddress ? (
                            <p className="mt-1 text-xs text-emerald-700">
                              Dirección aprobada: {addressValidationByIndex[index].validatedAddress}
                            </p>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleGeocodeAddress(index)}
                          className="inline-flex items-center gap-2 rounded-2xl border border-cyan-200 bg-white px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-50"
                        >
                          <Icon path="M12 2.25c-4.97 0-9 3.806-9 8.5 0 6.396 8.196 10.517 8.545 10.69a1 1 0 00.91 0C12.804 21.267 21 17.146 21 10.75c0-4.694-4.03-8.5-9-8.5z" className="h-4 w-4" />
                          Validar ubicación en mapa
                        </button>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span
                          className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
                            addr.latlng
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : 'border-slate-200 bg-white text-slate-500'
                          }`}
                        >
                          {addr.latlng
                            ? `Lat ${Number(addr.latlng.lat || 0).toFixed(5)} · Lng ${Number(
                                addr.latlng.lng || 0
                              ).toFixed(5)}`
                            : 'Todavía sin coordenadas validadas'}
                        </span>
                        {!GEOAPIFY_API_KEY ? (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold text-amber-700">
                            Sin clave de sugerencias automáticas
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                    Personas de contacto
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    Guardá responsables o referencias rápidas para la operación.
                  </p>
                </div>

                <button
                  onClick={() => addField('contacts')}
                  className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
                >
                  <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                  Añadir contacto
                </button>
              </div>

              <div className="mt-4 space-y-4">
                {formData.contacts.map((c, index) => (
                  <div
                    key={index}
                    className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <Badge tone="slate">Contacto {index + 1}</Badge>

                      {formData.contacts.length > 1 && (
                        <button
                          onClick={() => removeField(index, 'contacts')}
                          className="text-sm font-semibold text-rose-600 transition hover:text-rose-700"
                        >
                          Eliminar contacto
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Nombre
                        </label>
                        <input
                          type="text"
                          value={c.name}
                          onChange={(e) => handleFieldChange(e, index, 'name', 'contacts')}
                          placeholder="Nombre del contacto"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Teléfono
                        </label>
                        <input
                          type="text"
                          value={c.phone}
                          onChange={(e) => handleFieldChange(e, index, 'phone', 'contacts')}
                          placeholder="Teléfono"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 px-6 py-5">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-200"
          >
            Cancelar
          </button>
          <div className="flex flex-wrap items-center gap-3">
            {!isNew && typeof onDelete === 'function' ? (
              <button
                onClick={onDelete}
                className="inline-flex items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700 transition hover:bg-rose-100"
              >
                Eliminar
              </button>
            ) : null}
            <button
              onClick={handleSave}
              className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700"
            >
              Guardar registro
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =========================
// Manager
// =========================
const ContactsManager = ({ onSelectContact, showMessage, companyId: companyIdProp }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState('cliente');
  const [searchTerm, setSearchTerm] = useState('');

  const activeCompanyId = companyIdProp || getActiveCompanyId() || DEFAULT_COMPANY_ID;

  const companyContactsPath = useMemo(
    () => resolveCompanyContactsPathSafe(activeCompanyId),
    [activeCompanyId]
  );

  const legacyContactsPath = useMemo(
    () => `artifacts/${appId}/public/data/contacts`,
    []
  );

  useEffect(() => {
    if (!db || !appId) return;

    let companyDocs = [];
    let legacyDocs = [];
    let companyReady = false;
    let legacyReady = companyContactsPath === legacyContactsPath;
    let isCancelled = false;

    const normalizeSnapshotDocs = (snapshot, source) =>
      snapshot.docs.map((docSnap) => {
        const data = docSnap.data();

        return {
          id: docSnap.id,
          ...data,
          type: normalizeType(data.type),
          addresses: normalizeAddresses(data.addresses),
          contacts: normalizeContactPeople(data.contacts),
          _source: source
        };
      });

    const syncContacts = () => {
      if (!companyReady || !legacyReady || isCancelled) return;

      const merged = mergeContacts(companyDocs, legacyDocs);
      setContacts(merged);
      setLoading(false);
    };

    setLoading(true);

    const unsubscribeCompany = onSnapshot(
      query(collection(db, companyContactsPath)),
      (snapshot) => {
        companyReady = true;
        companyDocs = normalizeSnapshotDocs(snapshot, 'company');
        syncContacts();
      },
      (error) => {
        companyReady = true;
        companyDocs = [];
        console.error('Error cargando contactos de empresa:', error);
        syncContacts();
      }
    );

    let unsubscribeLegacy = () => {};

    if (companyContactsPath !== legacyContactsPath) {
      unsubscribeLegacy = onSnapshot(
        query(collection(db, legacyContactsPath)),
        (snapshot) => {
          legacyReady = true;
          legacyDocs = normalizeSnapshotDocs(snapshot, 'legacy');
          syncContacts();
        },
        (error) => {
          legacyReady = true;
          legacyDocs = [];
          console.error('Error cargando contactos legacy:', error);
          syncContacts();
        }
      );
    }

    return () => {
      isCancelled = true;
      unsubscribeCompany();
      unsubscribeLegacy();
    };
  }, [activeCompanyId, companyContactsPath, legacyContactsPath]);

  const handleSaveContact = async (data, isNew) => {
    try {
      const companyPath = companyContactsPath;

      const payload = {
        ...data,
        name: normalizeString(data.name),
        type: normalizeType(data.type),
        addresses: sanitizeAddressesForSave(data.addresses),
        contacts: sanitizeContactsForSave(data.contacts),
        companyId: activeCompanyId,
        updatedAt: serverTimestamp()
      };

      if (isNew) {
        await addDoc(collection(db, companyPath), {
          ...payload,
          createdAt: serverTimestamp()
        });
        showMessage?.('Contacto creado con éxito.', 'success');
      } else {
        await setDoc(doc(db, companyPath, editingContact.id), payload, { merge: true });
        showMessage?.('Contacto actualizado con éxito.', 'success');
      }

      setShowModal(false);
      setEditingContact(null);
    } catch (error) {
      console.error('Error guardando contacto:', error);
      showMessage?.('Error al guardar el contacto.', 'error');
    }
  };

  const handleSingleClick = (contact) => {
    setEditingContact(contact);
    setShowModal(true);
  };

  const handleDragStart = (e, contact) => {
    e.dataTransfer.setData('application/json', JSON.stringify(contact));
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingContact(null);
  };

  const handleDeleteContact = async (contact = editingContact) => {
    if (!contact?.id) return;

    if (contact._source === 'legacy') {
      showMessage?.(
        'Este registro viene de la base legacy y no se elimina desde esta vista.',
        'warning'
      );
      return;
    }

    const confirmed = window.confirm(
      `¿Eliminar "${contact.name || 'este registro'}" de la base de datos?`
    );

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, companyContactsPath, contact.id));
      showMessage?.('Registro eliminado con éxito.', 'success');
      setShowModal(false);
      setEditingContact(null);
    } catch (error) {
      console.error('Error eliminando contacto:', error);
      showMessage?.('No se pudo eliminar el registro.', 'error');
    }
  };

  const filteredContacts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return contacts;

    return contacts.filter((contact) => {
      const inName = contact.name?.toLowerCase().includes(term);
      const inAddress = (contact.addresses || []).some((addr) =>
        [addr.address, addr.locality, addr.partido, addr.province, addr.postalCode]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(term)
      );
      const inPeople = (contact.contacts || []).some((c) =>
        [c.name, c.phone].filter(Boolean).join(' ').toLowerCase().includes(term)
      );

      return inName || inAddress || inPeople;
    });
  }, [contacts, searchTerm]);

  const availableTypes = useMemo(() => {
    const typeSet = new Set(COMMON_TYPES);
    contacts.forEach((contact) => {
      const normalized = normalizeType(contact?.type);
      if (normalized) typeSet.add(normalized);
    });
    return Array.from(typeSet);
  }, [contacts]);

  const counts = useMemo(
    () =>
      availableTypes.reduce((accumulator, type) => {
        accumulator[type] = contacts.filter((contact) => normalizeType(contact.type) === type).length;
        return accumulator;
      }, {}),
    [availableTypes, contacts]
  );

  const groupedContacts = useMemo(
    () =>
      availableTypes.map((type) => ({
        type,
        items: filteredContacts.filter((contact) => normalizeType(contact.type) === type)
      })),
    [availableTypes, filteredContacts]
  );

  useEffect(() => {
    if (activeAccordion === null) return;

    if (!availableTypes.includes(activeAccordion)) {
      setActiveAccordion(availableTypes[0] || 'cliente');
    }
  }, [activeAccordion, availableTypes]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500">
              Base de datos
            </p>
            <h3 className="mt-2 text-2xl font-bold tracking-[-0.03em] text-slate-900">
              Puntos operativos
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              Clientes, proveedores, transportes u otros puntos operativos que podés editar y organizar para reutilizar en tus rutas.
            </p>
          </div>

          <button
            onClick={() => {
              setEditingContact(null);
              setShowModal(true);
            }}
            className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700"
            title="Añadir nuevo contacto"
          >
            <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-5 w-5" />
            Nuevo
          </button>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {availableTypes.slice(0, 3).map((type) => {
            const toneClass = resolveTypeTone(type);
            return (
              <div key={type} className={`min-w-0 rounded-2xl border px-3 py-3 ${toneClass}`}>
                <p className="break-words text-[10px] font-semibold uppercase tracking-[0.10em] sm:text-[11px] sm:tracking-[0.18em]">
                  {pluralizeTypeLabel(type)}
                </p>
                <p className="mt-2 break-words text-xl font-bold text-slate-900 sm:text-2xl">
                  {counts[type] || 0}
                </p>
              </div>
            );
          })}
        </div>

        <div className="relative mt-4">
          <Icon
            path="M15.75 15.75L21 21m-9-1.5a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre, dirección o contacto"
            className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center rounded-[24px] border border-slate-200 bg-slate-50 text-sm text-slate-500">
          Cargando registros...
        </div>
      ) : (
        <div className="flex-grow space-y-3 overflow-y-auto pr-1">
          {groupedContacts.map(({ type, items: typeContacts }) => {
            const isOpen = activeAccordion === type;
            const typeTone = resolveTypeTone(type);
            return (
              <div
                key={type}
                className={`overflow-hidden rounded-[24px] border transition-all duration-300 ${
                  isOpen
                    ? 'border-slate-300 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] shadow-[0_18px_40px_rgba(15,23,42,0.08)]'
                    : 'border-slate-200 bg-[linear-gradient(180deg,#f8fafc_0%,#f1f5f9_100%)]'
                }`}
              >
                <button
                  onClick={() => setActiveAccordion((prev) => (prev === type ? null : type))}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition ${
                    isOpen
                      ? 'bg-white/80'
                      : 'hover:bg-white/60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold shadow-sm ${typeTone}`}
                    >
                      {formatTypeLabel(type)}
                    </span>
                    <div>
                      <p className="font-semibold capitalize tracking-[-0.01em] text-slate-800">
                        {pluralizeTypeLabel(type)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {typeContacts.length}{' '}
                        {typeContacts.length === 1 ? 'registro visible' : 'registros visibles'}
                      </p>
                    </div>
                  </div>

                  <span
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-all ${
                      isOpen
                        ? 'border-slate-200 bg-white text-slate-700 shadow-[0_10px_22px_rgba(15,23,42,0.08)]'
                        : 'border-slate-200 bg-slate-100 text-slate-500'
                    }`}
                  >
                    <Icon
                      path={
                        isOpen
                          ? 'M19.5 8.25l-7.5 7.5-7.5-7.5'
                          : 'M8.25 4.5l7.5 7.5-7.5 7.5'
                      }
                      className={`h-5 w-5 transition-transform duration-300 ${isOpen ? 'translate-y-0.5' : ''}`}
                    />
                  </span>
                </button>

                {isOpen && (
                  <div className="space-y-2 border-t border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.82)_0%,rgba(248,250,252,0.96)_100%)] px-3 py-3">
                    {typeContacts.length > 0 ? (
                      typeContacts.map((contact) => {
                        const firstAddress = contact.addresses?.[0];
                        const peopleCount = contact.contacts?.filter(
                          (c) => c.name || c.phone
                        ).length;

                        return (
                          <div
                            key={contact.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSingleClick(contact)}
                            onDoubleClick={() => onSelectContact?.(contact)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleSingleClick(contact);
                              }
                            }}
                            draggable="true"
                            onDragStart={(e) => handleDragStart(e, contact)}
                            className="group w-full cursor-pointer rounded-[20px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-3 text-left shadow-[0_10px_24px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:border-blue-200 hover:bg-[linear-gradient(180deg,#ffffff_0%,#eff6ff_100%)] hover:shadow-[0_14px_32px_rgba(59,130,246,0.12)] focus:outline-none focus:ring-4 focus:ring-blue-100"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="truncate font-semibold text-slate-800">
                                    {contact.name}
                                  </p>

                                  {contact._source === 'legacy' && (
                                    <Badge tone="slate">Legacy</Badge>
                                  )}
                                </div>

                                {firstAddress?.address && (
                                  <p className="mt-1 text-sm text-slate-500">
                                    {firstAddress.address}
                                  </p>
                                )}

                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  {firstAddress?.locality ? (
                                    <Badge tone="slate">{firstAddress.locality}</Badge>
                                  ) : null}

                                  {(contact.addresses || []).length > 1 ? (
                                    <Badge tone="blue">
                                      {(contact.addresses || []).length} sucursales
                                    </Badge>
                                  ) : null}

                                  {peopleCount > 0 ? (
                                    <Badge tone="violet">
                                      {peopleCount}{' '}
                                      {peopleCount === 1 ? 'contacto' : 'contactos'}
                                    </Badge>
                                  ) : null}
                                </div>
                              </div>

                              <div className="flex shrink-0 items-center gap-1 opacity-0 transition group-hover:opacity-100">
                                {contact._source !== 'legacy' ? (
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleDeleteContact(contact);
                                    }}
                                    className="inline-flex items-center justify-center rounded-full border border-rose-100 bg-white p-2 text-rose-500 shadow-[0_8px_18px_rgba(244,63,94,0.10)] transition hover:bg-rose-50 hover:text-rose-600"
                                    aria-label="Eliminar registro"
                                    title="Eliminar registro"
                                  >
                                    <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                                  </button>
                                ) : null}
                                <span className="rounded-full border border-blue-100 bg-white px-2 py-1 text-[11px] font-semibold text-blue-700 shadow-[0_8px_18px_rgba(59,130,246,0.12)]">
                                  usar
                                </span>
                                <Icon
                                  path="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                  className="h-5 w-5 text-blue-500"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-500 shadow-[0_10px_20px_rgba(15,23,42,0.04)]">
                        No hay {pluralizeTypeLabel(type).toLowerCase()} visibles con este filtro.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <ContactModal
          contact={editingContact}
          onSave={handleSaveContact}
          onClose={handleCloseModal}
          onDelete={() => handleDeleteContact(editingContact)}
          showMessage={showMessage}
          availableTypes={availableTypes}
        />
      )}
    </div>
  );
};

export default ContactsManager;
